/**
 * Firm configuration and administration.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import * as authApi from '../api/Auth';
import * as firmApi from '../api/Firm';
import { parsed } from '../api/parse';
import type { ModuleCode } from '../features/modules/modules';
import {
	activityEntrySchema,
	analysisDefinitionSchema,
	approvalItemSchema,
	canonicalFieldSchema,
	clientAnalysisSchema,
	clientRecordSchema,
	firmAliasSchema,
	firmAnalysisSettingSchema,
	inviteRowSchema,
	layoutRecordSchema,
	moduleInfoSchema,
	mappingViewSchema,
	memberRoleSchema,
	rulesetPackSchema,
	userSchema,
	wireClassificationRuleSchema,
	type Role,
	type UserStatus,
} from '../schemas';
import { keys } from './keys';

/* ── reference data ─────────────────────────────────────────────────────── */

/**
 * The canonical schema's fields.
 *
 * `staleTime: Infinity` — this is the product's own vocabulary, defined in
 * server config. It does not change while somebody has the page open.
 */
/** The modules the product offers. Server-defined, so it does not go stale. */
export function useModulesQuery() {
	return useQuery({
		queryKey: keys.modules.all(),
		queryFn: async () =>
			parsed(
				z.array(moduleInfoSchema),
				await firmApi.getModules(),
				'/reference/modules',
			),
		staleTime: Infinity,
	});
}

/** The mapping targets of one module (absent = the server's default, O2C). */
export function useCanonicalFieldsQuery(module?: ModuleCode) {
	return useQuery({
		queryKey: [
			'reference',
			'canonical-fields',
			module ?? 'default',
		] as const,
		queryFn: async () =>
			parsed(
				z.object({ fields: z.array(canonicalFieldSchema) }),
				await firmApi.getCanonicalFields(module),
				'/reference/canonical-fields',
			),
		staleTime: Infinity,
	});
}

export function useAnalysisDefinitionsQuery(module?: ModuleCode) {
	return useQuery({
		queryKey: ['analyses', 'definitions', module ?? 'default'] as const,
		queryFn: async () =>
			parsed(
				z.array(analysisDefinitionSchema),
				await firmApi.getAnalysisDefinitions(module),
				'/analyses',
			),
		staleTime: Infinity,
	});
}

/**
 * Admin only: the firm's exception-calculation setting per analysis.
 *
 * `enabled` so a non-admin visiting the Analyses tab (reachable by anyone
 * with `mapFormats`, see `FirmConfiguration.tsx`) never fires a request the
 * server would 403.
 */
export function useFirmAnalysisSettingsQuery(
	enabled: boolean,
	module?: ModuleCode,
) {
	return useQuery({
		queryKey: keys.firm.analysisSettings(module),
		/* The internal reports with their settings, and the client's analyses
       (#1-#34) that group them: the screen shows one row per analysis. */
		queryFn: async () =>
			parsed(
				z.object({
					analyses: z.array(firmAnalysisSettingSchema),
					clientAnalyses: z.array(clientAnalysisSchema),
				}),
				await firmApi.getAnalysisSettings(module),
				'/firm/analysis-settings',
			),
		enabled,
	});
}

export function useUpdateAnalysisSettingMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			slug: string;
			exceptionCalculationEnabled: boolean;
		}) =>
			parsed(
				firmAnalysisSettingSchema,
				await firmApi.updateAnalysisSetting(
					input.slug,
					input.exceptionCalculationEnabled,
				),
				`/firm/analysis-settings/${input.slug}`,
			),
		onSuccess: () => {
			/* Every module's list: a setting belongs to one slug, whichever module it is in. */
			void client.invalidateQueries({
				queryKey: keys.firm.analysisSettingsAll,
			});
		},
	});
}

/* ── firm configuration ─────────────────────────────────────────────────── */

export function useFirmAliasesQuery(enabled: boolean, module?: ModuleCode) {
	return useQuery({
		queryKey: keys.firm.aliases(module),
		queryFn: async () =>
			parsed(
				z.object({ aliases: z.array(firmAliasSchema) }),
				await firmApi.getFirmAliases(module),
				'/firm/aliases',
			),
		enabled,
	});
}

/**
 * The firm's classification rules at one ruleset status.
 *
 * The status is part of the key rather than a filter applied after the fact:
 * ACTIVE is what the engine runs and DRAFT is what an admin is editing, and
 * they are genuinely different lists, not two views of one.
 */
export function useFirmRulesQuery(
	status: 'ACTIVE' | 'DRAFT',
	enabled = true,
	module?: ModuleCode,
) {
	return useQuery({
		queryKey: keys.firm.classificationRules(status, module),
		queryFn: async () =>
			parsed(
				z.object({ rules: z.array(wireClassificationRuleSchema) }),
				await firmApi.getFirmRules(status, module),
				'/firm/classification-rules',
			),
		enabled,
	});
}

export function useRulesetsQuery(module?: ModuleCode) {
	return useQuery({
		queryKey: keys.firm.rulesets(module),
		/* The server wraps the list: `{ rulesets: [...] }`. */
		queryFn: async () =>
			parsed(
				z.object({ rulesets: z.array(rulesetPackSchema) }),
				await firmApi.getRulesets(module),
				'/firm/rulesets',
			).rulesets,
	});
}

/** Everything a rule change touches: the packs, and both status views. */
function invalidateRules(client: ReturnType<typeof useQueryClient>) {
	void client.invalidateQueries({ queryKey: ['firm', 'rulesets'] });
	void client.invalidateQueries({
		queryKey: ['firm', 'classification-rules'],
	});
}

export function usePublishRulesetMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (rulesetId: string) => firmApi.publishRuleset(rulesetId),
		onSuccess: () => invalidateRules(client),
	});
}

export function useDeleteRuleMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (ruleId: string) => firmApi.deleteRule(ruleId),
		onSuccess: () => invalidateRules(client),
	});
}

/**
 * Write one rule.
 *
 * POST or PATCH is the only difference between creating and editing — the
 * body is decided by the caller's `leafPayload`, which is the one place that
 * knows whether a rule travels as a flat triple or a triple plus a tree.
 */
export function useSaveRuleMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: {
			ruleId?: string;
			body: object;
			module?: ModuleCode;
		}) =>
			input.ruleId
				? firmApi.updateRule(input.ruleId, input.body)
				: firmApi.createRule(input.body, input.module),
		onSuccess: () => invalidateRules(client),
	});
}

export function useConfirmMappingMutation(fileId: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (body: unknown) => firmApi.confirmMapping(fileId, body),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ['mapping'] });
			void client.invalidateQueries({
				queryKey: keys.files.detail(fileId),
			});
		},
	});
}

/* ── administration ─────────────────────────────────────────────────────── */

export function useMembersQuery() {
	return useQuery({
		queryKey: ['firm', 'members'] as const,
		queryFn: async () =>
			parsed(
				z.object({
					users: z.array(userSchema),
					roles: z.array(memberRoleSchema),
				}),
				await firmApi.getMembers(),
				'/users',
			),
	});
}

export function useInvitesQuery() {
	return useQuery({
		queryKey: ['auth', 'invites'] as const,
		queryFn: async () =>
			parsed(
				z.object({ invites: z.array(inviteRowSchema) }),
				await firmApi.getInvites(),
				'/auth/invites',
			),
	});
}

export function useUpdateUserMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { id: string; role?: Role; status?: UserStatus }) =>
			firmApi.updateUser(input.id, input.role, input.status),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ['firm', 'members'] });
		},
	});
}

export function useRevokeInviteMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => firmApi.revokeInvite(id),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ['auth', 'invites'] });
		},
	});
}

export function useCreateUserMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (body: {
			name: string;
			email: string;
			password: string;
			role: Role;
		}) => parsed(userSchema, await firmApi.createUser(body), '/users'),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ['firm', 'members'] });
		},
	});
}

export function useCreateInviteMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (body: { email: string; role: Role }) =>
			parsed(
				z.object({ accept_url: z.string() }),
				await firmApi.createInvite(body),
				'/auth/invites',
			),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ['auth', 'invites'] });
		},
	});
}

/* ── approvals, activity, clients, mapping, password ────────────────────── */

export function useApprovalsQuery() {
	return useQuery({
		queryKey: ['approvals'] as const,
		queryFn: async () =>
			parsed(
				z.object({ approvals: z.array(approvalItemSchema) }),
				await firmApi.getApprovals(),
				'/approvals',
			),
	});
}

export function useActivityQuery() {
	return useQuery({
		queryKey: ['activity'] as const,
		queryFn: async () =>
			parsed(
				z.object({ activity: z.array(activityEntrySchema) }),
				await firmApi.getActivity(),
				'/activity',
			),
	});
}

/** A new client is in every module. */
export function useCreateClientMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (body: unknown) =>
			parsed(
				clientRecordSchema,
				await firmApi.createClient(body),
				'/clients',
			),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: keys.clients.all });
		},
	});
}

export function useClientLayoutsQuery(clientId: string | null) {
	return useQuery({
		queryKey: ['clients', clientId ?? '', 'layouts'] as const,
		queryFn: async () =>
			parsed(
				z.array(layoutRecordSchema),
				await firmApi.getClientLayouts(clientId as string),
				`/clients/${clientId}/layouts`,
			),
		enabled: Boolean(clientId),
	});
}

export function useMappingQuery(path: string | null) {
	return useQuery({
		queryKey: ['mapping', path ?? ''] as const,
		queryFn: async () =>
			parsed(
				mappingViewSchema,
				await firmApi.getMapping(path as string),
				path as string,
			),
		enabled: Boolean(path),
	});
}

export function useChangePasswordMutation() {
	return useMutation({
		mutationFn: (input: { currentPassword: string; newPassword: string }) =>
			authApi.changePassword(input.currentPassword, input.newPassword),
	});
}
